import React, { ReactElement, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  Drawer,
  Form,
  InputNumber,
  notification,
  Row,
  Space,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { FieldData } from 'rc-field-form/lib/interface';

import { ContractTerm } from 'pricing/types';
import { useContractState } from 'pricing/components/contract/useContractState/useContractState';
import { contractTermCreate } from 'pricing/components/contractTerms/contractTermForm/utilities/contractTermCreate';
import { contractTermEdit } from 'pricing/components/contractTerms/contractTermForm/utilities/contractTermEdit';
import { contractTermFormSetFieldsValue } from 'pricing/components/contractTerms/contractTermForm/utilities/contractTermFormSetFieldsValue';
import { contractUploadMediaAmendment } from 'pricing/components/contract/contractForm/utilities/contractUploadMediaAmendment';
import { isContractEditMode } from 'pricing/utilities/contract/contractMode/contractMode';
import { testId } from 'pricing/utilities/test/dataTestId';
import FieldContractTermTypes from 'pricing/components/contractTerms/contractTermForm/fields/fieldContractTermTypes/FieldContractTermTypes';
import FieldBreeds from 'pricing/components/contractTerms/contractTermForm/fields/fieldBreeds/FieldBreeds';
import FieldPuppyColors from 'pricing/components/contractTerms/contractTermForm/fields/fieldPuppyColors/FieldPuppyColors';
import FieldPuppyVarieties from 'pricing/components/contractTerms/contractTermForm/fields/fieldPuppyVarieties/FieldPuppyVarieties';
import FieldCurrency from 'pricing/components/contractTerms/contractTermForm/fields/fieldCurrency/FieldCurrency';
import {
  ContractTermFormDrawerFooter,
  ContractTermsFormStyled,
} from './ContractTermForm.style';

function ContractTermForm(): ReactElement {
  const [addAndCreateNew, setAddAndCreateNew] = useState(false);
  const [contractTermIsSaving, setContractTermIsSaving] = useState(false);
  const { state, dispatch } = useContractState();
  const {
    contract,
    contractMode,
    termsFormVisible,
    editContractTerm,
    amendmentFileUpload,
    hasAmendmentUploaded,
  } = state;
  const [form] = Form.useForm();

  const handleCloseDrawer = () => {
    dispatch({
      type: 'SET_TERMS_FORM_VISIBLE',
      termsFormVisible: false,
    });
    dispatch({
      type: 'SET_EDIT_TERM',
      editContractTerm: null,
    });
    form.resetFields();
    setAddAndCreateNew(false);
  };

  const handleAddAndCreateNew = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    setAddAndCreateNew(checked);
  };

  const handleSubmitContractTerm = async () => {
    try {
      setContractTermIsSaving(true);
      if (!contract?.id) {
        throw Error(`No contract assigned.`);
      }

      await form.validateFields();

      const contractTermsFormValues = form.getFieldsValue();
      let respContractTerm = {} as ContractTerm;
      if (isContractEditMode(contractMode)) {
        if (amendmentFileUpload && !hasAmendmentUploaded) {
          await contractUploadMediaAmendment(contract.id, amendmentFileUpload);
          dispatch({
            type: 'SET_HAS_AMENDMENT_UPLOADED',
            hasAmendmentUploaded: true,
          });
        }

        if (!editContractTerm) {
          respContractTerm = await contractTermCreate(
            contract.id,
            contractTermsFormValues,
            true,
          );
        } else {
          respContractTerm = await contractTermEdit(
            contractTermsFormValues,
            editContractTerm,
            true,
          );
        }
      } else {
        if (editContractTerm) {
          respContractTerm = await contractTermEdit(
            contractTermsFormValues,
            editContractTerm,
          );
        } else {
          respContractTerm = await contractTermCreate(
            contract.id,
            contractTermsFormValues,
          );
        }
      }

      if (addAndCreateNew) {
        form.resetFields();
        setAddAndCreateNew(false);
        dispatch({
          type: 'SET_EDIT_TERM',
          editContractTerm: null,
        });
      } else {
        handleCloseDrawer();
      }

      setContractTermIsSaving(false);

      dispatch({
        type: 'SET_CONTRACT_TERM',
        contractTerm: respContractTerm,
      });

      notification.success({
        message: `Contract Term ${editContractTerm ? 'edited' : 'added'}`,
      });
    } catch (e) {
      setContractTermIsSaving(false);
      notification.error({
        message: `Error sending contract term`,
        description: e.message,
      });
    }
  };

  const handleOnFieldsChange = (changedFields: FieldData[]) => {
    const [{ name }] = changedFields;

    if (typeof name === 'object') {
      if (name[0] === 'breedId') {
        form.resetFields(['colorIds', 'varietyIds']);
      }
    }
  };

  useEffect(() => {
    if (editContractTerm) {
      form.setFieldsValue(contractTermFormSetFieldsValue(editContractTerm));
    }
  }, [editContractTerm]);

  return (
    <Drawer
      data-testid={testId.contractTermDrawer}
      title={`${editContractTerm ? 'Edit ' : 'Add '} Term`}
      visible={termsFormVisible}
      onClose={handleCloseDrawer}
      maskClosable={false}
      width="485px"
      footer={
        <ContractTermFormDrawerFooter>
          <Checkbox
            data-testid={testId.checkboxContractTermAddAndCreateNew}
            checked={addAndCreateNew}
            onChange={handleAddAndCreateNew}
          >
            {editContractTerm ? 'Edit ' : 'Add '} and create new
          </Checkbox>
          <Space size={15} align="center">
            <Button onClick={handleCloseDrawer} data-testid={testId.cancelContractTerm}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmitContractTerm}
              data-testid={testId.submitContractTerm}
              loading={contractTermIsSaving}
            >
              {editContractTerm ? 'Edit ' : 'Add '}
            </Button>
          </Space>
        </ContractTermFormDrawerFooter>
      }
    >
      <ContractTermsFormStyled
        data-testid={testId.contractTermForm}
        layout="vertical"
        form={form}
        hideRequiredMark
        onFieldsChange={handleOnFieldsChange}
      >
        <FieldContractTermTypes disabled={Boolean(editContractTerm)} />

        <FieldBreeds disabled={Boolean(editContractTerm)} />

        <FieldPuppyColors />

        <FieldPuppyVarieties />

        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[{ required: true, message: 'This field is required' }]}
        >
          <InputNumber min={0} data-testid={testId.fieldQuantity} type="number" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <FieldCurrency label="Initial Cost" name="initialCost" type="Cost" />
          </Col>
          <Col span={12}>
            <FieldCurrency label="Initial Price" name="initialPrice" type="Price" />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <FieldCurrency label="Middle Cost" name="middleCost" type="Cost" />
          </Col>
          <Col span={12}>
            <FieldCurrency label="Middle Price" name="middlePrice" type="Price" />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <FieldCurrency label="Final Cost" name="finalCost" type="Cost" />
          </Col>
          <Col span={12}>
            <FieldCurrency label="Final Price" name="finalPrice" type="Price" />
          </Col>
        </Row>
      </ContractTermsFormStyled>
    </Drawer>
  );
}

export default ContractTermForm;
